// ── Session colors (solid hex) ────────────────────────────────────────────────
export const BG_MAIN      = '#0F0F0F';
export const BG_PREMARKET = '#523A15';
export const BG_POSTMKT   = '#131C33';

// Timeframes shown in both charts
export const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1D'];

// Session coloring is only meaningful on intraday timeframes
export const SESSION_TF = new Set(['1m', '5m', '15m', '30m', '1h']);

// ── ET time formatters (reused across multiple charts) ────────────────────────
const etTimeFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit', hour12: false });
const etDateFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric' });
const etMonthFmt = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', year: 'numeric' });
const etYearFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric' });
const etFullFmt  = new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });

// ── Session classification (ET) ───────────────────────────────────────────────
export function getSessionType(unixTs) {
  const d  = new Date(unixTs * 1000);
  const et = new Date(d.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const m  = et.getHours() * 60 + et.getMinutes();
  if (m >= 570 && m < 960)  return 'regular';
  if (m >= 240 && m < 570)  return 'premarket';
  if (m >= 960 && m < 1200) return 'postmarket';
  return null;
}

// ── Day separator line timestamps ─────────────────────────────────────────────
// Returns the timestamp of the FIRST bar in each new ET calendar day.
// Works for any array with a .time property (bars, cap-series points, etc.).
export function buildDayLines(items) {
  if (!items || !items.length) return [];
  const lines = [];
  let prevDate = null;
  for (const item of items) {
    const etDate = new Date(item.time * 1000).toLocaleDateString('en-US', { timeZone: 'America/New_York' });
    if (prevDate !== null && etDate !== prevDate) lines.push(item.time);
    prevDate = etDate;
  }
  return lines;
}

// ── Day line primitive — draws a thin vertical rule at each day boundary ──────
// Attach AFTER any session-band primitive so the line renders on top of bands.
export class DayLinePrimitive {
  constructor() { this._times = []; this._chart = null; this._requestUpdate = null; }
  setLines(times) { this._times = times; if (this._requestUpdate) this._requestUpdate(); }
  clearLines()    { this._times = [];  if (this._requestUpdate) this._requestUpdate(); }
  attached({ chart, requestUpdate }) { this._chart = chart; this._requestUpdate = requestUpdate; }
  detached()      { this._chart = null; this._requestUpdate = null; }
  updateAllViews() {}
  paneViews()     { return [this._view()]; }
  _view() {
    const self = this;
    return {
      zOrder: () => 'bottom',
      renderer: () => ({
        draw: () => {},
        drawBackground: (target) => {
          if (!self._chart || !self._times.length) return;
          target.useMediaCoordinateSpace(scope => {
            const ctx = scope.context;
            const ts  = self._chart.timeScale();
            const W   = scope.mediaSize.width;
            const H   = scope.mediaSize.height;
            ctx.save();
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)';
            ctx.lineWidth   = 1;
            for (const t of self._times) {
              const x = ts.timeToCoordinate(t);
              if (x === null || x < 0 || x > W) continue;
              ctx.beginPath();
              ctx.moveTo(x, 0);
              ctx.lineTo(x, H);
              ctx.stroke();
            }
            ctx.restore();
          });
        },
      }),
    };
  }
}

// ── Build session bands from bar/point array (needs .time) ───────────────────
export function buildSessionBands(bars) {
  const bands = [];
  if (!bars || !bars.length) return bands;

  let curType = null, bandStart = null, bandEnd = null;

  const flush = () => {
    if (curType !== null) bands.push({ type: curType, start: bandStart, end: bandEnd + 60 });
  };

  for (const bar of bars) {
    const t = getSessionType(bar.time);
    if (t !== curType) { flush(); curType = t; bandStart = bar.time; }
    bandEnd = bar.time;
  }
  flush();
  return bands;
}

// ── Lightweight Charts v5 ISeriesPrimitive for session backgrounds ─────────────
//
//  IPrimitivePaneRenderer.drawBackground(target) renders behind all series
//  content. target.useMediaCoordinateSpace() gives CSS-pixel canvas context.
//
export class SessionBandPrimitive {
  constructor() {
    this._bands = [];
    this._chart = null;
    this._requestUpdate = null;
  }

  setBands(bands) {
    this._bands = bands;
    if (this._requestUpdate) this._requestUpdate();
  }

  clearBands() {
    this._bands = [];
    if (this._requestUpdate) this._requestUpdate();
  }

  attached({ chart, requestUpdate }) {
    this._chart = chart;
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._chart = null;
    this._requestUpdate = null;
  }

  updateAllViews() {}

  paneViews() { return [this._view()]; }

  _view() {
    const self = this;
    return {
      zOrder: () => 'bottom',
      renderer: () => ({
        draw: () => {},
        drawBackground: (target) => {
          if (!self._chart || !self._bands.length) return;
          target.useMediaCoordinateSpace(scope => {
            const ctx = scope.context;
            const ts  = self._chart.timeScale();
            const W   = scope.mediaSize.width;
            const H   = scope.mediaSize.height;

            for (const band of self._bands) {
              const color =
                band.type === 'premarket'  ? BG_PREMARKET :
                band.type === 'postmarket' ? BG_POSTMKT   : BG_MAIN;

              const x1 = ts.timeToCoordinate(band.start);
              const x2 = ts.timeToCoordinate(band.end);
              if (x1 === null && x2 === null) continue;

              const left  = x1 !== null ? Math.max(0, x1) : 0;
              const right = x2 !== null ? Math.min(W, x2) : W;
              if (right <= left) continue;

              ctx.fillStyle = color;
              ctx.fillRect(left, 0, right - left, H);
            }
          });
        },
      }),
    };
  }
}

// ── Shared chart creation options ─────────────────────────────────────────────
export function baseChartOptions(width) {
  return {
    layout: {
      background: { type: 'solid', color: BG_MAIN },
      textColor:  '#9ca3af',
      fontSize:   11,
    },
    grid: {
      vertLines: { color: '#1a1a1a' },
      horzLines: { color: '#1a1a1a' },
    },
    rightPriceScale: { borderColor: '#2d2d2d' },
    timeScale: {
      borderColor:    '#2d2d2d',
      timeVisible:    true,
      secondsVisible: false,
      rightOffset:    8,
      // Display all tick labels in US Eastern Time (24-hour)
      tickMarkFormatter: (time, tickMarkType) => {
        const d = new Date(time * 1000);
        if (tickMarkType >= 3) return etTimeFmt.format(d);          // HH:MM ET
        if (tickMarkType === 2) return etDateFmt.format(d);         // "Jun 26"
        if (tickMarkType === 1) return etMonthFmt.format(d);        // "Jun 2026"
        return etYearFmt.format(d);                                  // "2026"
      },
    },
    // Crosshair tooltip time in US Eastern Time (24-hour)
    localization: {
      timeFormatter: (time) => etFullFmt.format(new Date(time * 1000)),
    },
    crosshair: {
      vertLine: { color: '#555', width: 1, style: 0 },
      horzLine: { color: '#555', width: 1, style: 0 },
    },
    handleScroll: {
      mouseWheel:       true,
      pressedMouseMove: false, // handled by attachMousePan() — avoids conflict with React event delegation
      horzTouchDrag:    true,
      vertTouchDrag:    true,
    },
    handleScale: {
      mouseWheel: true,
      pinch:      true,
    },
    width,
    height: 400,
  };
}

// ── Timeframe button render helper ────────────────────────────────────────────
export function TfButtons({ tf, setTf }) {
  return (
    <div className="flex items-center gap-1">
      {TIMEFRAMES.map(t => (
        <button
          key={t}
          onClick={() => setTf(t)}
          className="text-xs font-medium px-2 py-1 rounded transition-colors"
          style={
            t === tf
              ? { backgroundColor: '#60a5fa22', color: '#60a5fa', border: '1px solid #60a5fa55' }
              : { backgroundColor: 'transparent', color: '#6b7280', border: '1px solid transparent' }
          }
        >
          {t}
        </button>
      ))}
    </div>
  );
}

// ── Custom desktop mouse-pan ──────────────────────────────────────────────────
// LWC's built-in pressedMouseMove pan can fail on desktop when React 17+
// event delegation sits between the chart canvas and document.documentElement
// (where LWC registers its mousemove listener).  This helper attaches its own
// mousedown → mousemove → mouseup cycle directly on the container element and
// calls the public scrollToPosition() API, which always works.
// Returns a cleanup function to remove all listeners on unmount.
export function attachMousePan(el, chart) {
  const drag = { active: false, startX: 0, startPos: 0 };

  const onMouseDown = (e) => {
    if (e.button !== 0) return;
    drag.active  = true;
    drag.startX  = e.clientX;
    drag.startPos = chart.timeScale().scrollPosition();
  };

  const onMouseMove = (e) => {
    if (!drag.active) return;
    const lr = chart.timeScale().getVisibleLogicalRange();
    if (!lr) return;
    const visibleBars = lr.to - lr.from;
    if (visibleBars <= 0) return;
    const barsPx = visibleBars / (el.clientWidth || 800); // bars per pixel
    const delta  = (e.clientX - drag.startX) * barsPx;   // positive = drag right = see newer data
    chart.timeScale().scrollToPosition(drag.startPos - delta, false);
  };

  const onMouseUp = () => { drag.active = false; };

  el.addEventListener('mousedown', onMouseDown);
  document.addEventListener('mousemove', onMouseMove);
  document.addEventListener('mouseup',   onMouseUp);

  return () => {
    el.removeEventListener('mousedown', onMouseDown);
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup',   onMouseUp);
  };
}

// ── Session legend ─────────────────────────────────────────────────────────────
export function SessionLegend() {
  return (
    <div className="ml-auto flex items-center gap-4 text-xs text-gray-500">
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: BG_PREMARKET, border: '1px solid #7a5520' }} />
        Pre-market
      </span>
      <span className="flex items-center gap-1.5">
        <span className="inline-block w-3 h-3 rounded-sm"
          style={{ backgroundColor: BG_POSTMKT, border: '1px solid #1e2d50' }} />
        Post-market
      </span>
    </div>
  );
}
